import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { AdminLayout } from '../AdminLayout';
import { analytics } from '../../utils/analytics';

import { getErrorMessage } from '../../utils/errorMessage';
import { Search, Loader2, AlertCircle, CheckCircle, Send, History, ChevronDown, ChevronUp, Database, Zap, Trash2, Eye, EyeOff, FileSpreadsheet, ExternalLink } from 'lucide-react';

interface ConversationMessage {
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
}

interface AgentQueryResponse {
  query: string;
  result: {
    formatted?: string;
    count?: number;
    table?: string;
    rows?: unknown[];
    students?: Array<{
      id: string;
      first_name: string;
      last_name: string;
      active: boolean;
      parents: Array<{
        first_name: string;
        last_name: string;
        email: string;
        relationship: string;
      }>;
      enrollments: Array<{
        program_name: string;
        program_type: string;
        is_active: boolean;
      }>;
    }>;
    steps?: Array<{
      tool: string;
      params: Record<string, unknown>;
      result: unknown;
    }>;
    needsClarification?: boolean;
    error?: string;
  };
  reasoning: string[];
  queriesExecuted: string[];
  conversationHistory?: Array<{ role: string; parts: Array<{ text?: string }> }>;
}

interface HistoryItem {
  id: string;
  query: string;
  query_normalized: string;
  formatted_response: string;
  sql_queries: string[] | null;
  raw_result: AgentQueryResponse['result'] | null;
  is_sql_based: boolean;
  needs_clarification: boolean;
  created_at: string;
}

type AgentErrorBody = {
  error?: string;
  message?: string;
  details?: string;
  retryAfterSeconds?: number;
};

type ExportState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'done'; url: string; tabs: Array<{ name: string; rowCount: number; truncated: boolean }> }
  | { status: 'error'; message: string };

function extractRunSqlSteps(
  steps: AgentQueryResponse['result']['steps'] | undefined,
): Array<{ sql: string; label: string }> {
  const runSql = (steps ?? []).flatMap((s) => {
    if (s.tool !== 'run_sql') return [];
    const sql = typeof (s.params as Record<string, unknown>).sql === 'string'
      ? ((s.params as Record<string, unknown>).sql as string).trim()
      : '';
    return sql ? [{ sql }] : [];
  });
  const isSingle = runSql.length === 1;
  return runSql.map((s, i) => ({ sql: s.sql, label: isSingle ? 'Data' : `Query ${i + 1}` }));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function friendlyAgentError(status: number, body?: AgentErrorBody): string {
  const retryAfter =
    typeof body?.retryAfterSeconds === 'number' && Number.isFinite(body.retryAfterSeconds) && body.retryAfterSeconds > 0
      ? body.retryAfterSeconds
      : undefined;

  if (status === 429) {
    return `Rate limited. Please retry${retryAfter ? ` in ~${retryAfter.toFixed(1)}s` : ' shortly'}.`;
  }
  if (status === 503) {
    return `Service temporarily unavailable. Please try again${retryAfter ? ` in ~${retryAfter.toFixed(1)}s` : ''}.`;
  }
  if (status === 401) {
    return 'Not authenticated. Please log in again.';
  }
  if (status === 403) {
    return 'Forbidden. Admin access required.';
  }
  return body?.message || body?.error || `Request failed (HTTP ${status}).`;
}

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function isFailedResponse(text: string): boolean {
  const t = text.trim().toLowerCase();
  return (
    t.startsWith('i apologize') ||
    t.startsWith("i'm sorry") ||
    t.startsWith('i am sorry') ||
    t.startsWith('i cannot') ||
    t.startsWith("i can't") ||
    t.startsWith('unfortunately') ||
    t.startsWith('i was unable') ||
    t.startsWith("i don't have") ||
    t.startsWith('i do not have')
  );
}

function renderMarkdown(text: string) {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let tableLines: string[] = [];
  let key = 0;

  function renderInline(line: string): React.ReactNode {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
      part.startsWith('**') && part.endsWith('**')
        ? <strong key={i}>{part.slice(2, -2)}</strong>
        : part
    );
  }

  function parseTableRow(line: string): string[] {
    return line.split('|').slice(1, -1).map((cell) => cell.trim());
  }

  function isTableRow(line: string): boolean {
    return line.trim().startsWith('|') && line.trim().endsWith('|');
  }

  function isSeparatorRow(line: string): boolean {
    return /^\|[-\s|:]+\|$/.test(line.trim());
  }

  function flushTable() {
    if (tableLines.length < 2) {
      for (const l of tableLines) {
        elements.push(<p key={key++}>{l}</p>);
      }
      tableLines = [];
      return;
    }
    const headers = parseTableRow(tableLines[0]);
    const dataRows = tableLines.slice(2).map(parseTableRow);
    elements.push(
      <div key={key++} className="overflow-x-auto overflow-y-auto my-2">
        <table className="min-w-full text-sm border border-gray-200 rounded">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              {headers.map((h, i) => (
                <th key={i} className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide border-b border-gray-200">
                  {renderInline(h)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {dataRows.map((row, ri) => (
              <tr key={ri} className="hover:bg-gray-50">
                {row.map((cell, ci) => (
                  <td key={ci} className="px-3 py-2 text-gray-800 whitespace-nowrap">
                    {renderInline(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    tableLines = [];
  }

  function flushList() {
    if (listItems.length > 0) {
      elements.push(
        <ul key={key++} className="list-disc list-inside space-y-1 my-1">
          {listItems.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      listItems = [];
    }
  }

  for (const line of lines) {
    if (isTableRow(line)) {
      flushList();
      if (!isSeparatorRow(line) || tableLines.length > 0) {
        tableLines.push(line);
      }
      continue;
    }

    if (tableLines.length > 0) {
      flushTable();
    }

    const bulletMatch = /^[-*]\s+(.*)/.exec(line);
    const headingMatch = /^#{1,3}\s+(.*)/.exec(line);

    if (bulletMatch) {
      listItems.push(bulletMatch[1]);
    } else if (headingMatch) {
      flushList();
      elements.push(
        <p key={key++} className="font-semibold mt-2">{renderInline(headingMatch[1])}</p>
      );
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      elements.push(<p key={key++}>{renderInline(line)}</p>);
    }
  }

  flushList();
  flushTable();

  return <div className="space-y-1">{elements}</div>;
}

export function AdminAgentSearch() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AgentQueryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [introTimestamp] = useState(() => new Date());
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [suggestions, setSuggestions] = useState<HistoryItem[]>([]);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [recentHistory, setRecentHistory] = useState<HistoryItem[]>([]);
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);
  const [exportState, setExportState] = useState<ExportState>({ status: 'idle' });
  const [lastSubmittedQuery, setLastSubmittedQuery] = useState('');

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const suggestionsRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-grow textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = '0px';
    const next = Math.min(el.scrollHeight, 220);
    el.style.height = `${Math.max(next, 48)}px`;
  }, [query]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  // Fetch current user once on mount
  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setCurrentUser(data.user));
  }, []);

  // Fetch recent history on mount
  useEffect(() => {
    void supabase
      .from('agent_query_history')
      .select('id, query, query_normalized, formatted_response, sql_queries, raw_result, is_sql_based, needs_clarification, created_at')
      .order('created_at', { ascending: false })
      .limit(15)
      .then(({ data }) => {
        if (data) setRecentHistory(data as HistoryItem[]);
      });
  }, []);

  // Debounced autocomplete
  const fetchSuggestions = useCallback((value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const normalized = value.trim().toLowerCase();
    if (normalized.length < 2) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      void supabase
        .from('agent_query_history')
        .select('id, query, query_normalized, formatted_response, sql_queries, raw_result, is_sql_based, needs_clarification, created_at')
        .ilike('query_normalized', `%${normalized}%`)
        .order('created_at', { ascending: false })
        .limit(6)
        .then(({ data }) => {
          setSuggestions((data as HistoryItem[]) ?? []);
          setActiveSuggestion(-1);
        });
    }, 300);
  }, []);

  function handleQueryChange(value: string) {
    setQuery(value);
    fetchSuggestions(value);
  }

  function showCachedResult(item: HistoryItem) {
    const userMessage: ConversationMessage = {
      role: 'user',
      text: item.query,
      timestamp: new Date(),
    };
    const assistantMessage: ConversationMessage = {
      role: 'assistant',
      text: item.formatted_response,
      timestamp: new Date(),
    };
    setConversation((prev) => [...prev, userMessage, assistantMessage]);
    setQuery('');
    setSuggestions([]);
    if (item.raw_result) {
      setResult({ query: item.query, result: item.raw_result, reasoning: [], queriesExecuted: [] });
    }
  }

  async function deleteHistoryItem(id: string) {
    await supabase.from('agent_query_history').delete().eq('id', id);
    setRecentHistory((prev) => prev.filter((item) => item.id !== id));
  }

  async function saveToHistory(userQuery: string, agentData: AgentQueryResponse, priorConversation: ConversationMessage[]) {
    const sqlQueries = agentData.result.steps
      ?.filter((s) => s.tool === 'run_sql')
      .map((s) => (s.params as { sql?: string }).sql)
      .filter((s): s is string => !!s) ?? [];

    const { error: saveError } = await supabase.from('agent_query_history').upsert(
      {
        query: userQuery,
        query_normalized: userQuery.trim().toLowerCase(),
        formatted_response: agentData.result.formatted ?? '',
        sql_queries: sqlQueries.length > 0 ? sqlQueries : null,
        raw_result: agentData.result,
        is_sql_based: sqlQueries.length > 0,
        needs_clarification: agentData.result.needsClarification ?? false,
        conversation_history: priorConversation.length > 0 ? priorConversation : null,
      },
      { onConflict: 'query_normalized' }
    );

    if (!saveError && agentData.result.formatted) {
      // Refresh recent history
      const { data } = await supabase
        .from('agent_query_history')
        .select('id, query, query_normalized, formatted_response, sql_queries, raw_result, is_sql_based, needs_clarification, created_at')
        .order('created_at', { ascending: false })
        .limit(15);
      if (data) setRecentHistory(data as HistoryItem[]);
    }
  }

  async function handleSearch(overrideQuery?: string) {
    const userQuery = (overrideQuery ?? query).trim();
    if (!userQuery) return;

    setHistoryOpen(false);
    setSuggestions([]);
    setActiveSuggestion(-1);
    setExportState({ status: 'idle' });
    setLastSubmittedQuery(userQuery);

    // Cache-hit: exact match on non-SQL result → return immediately
    const normalized = userQuery.toLowerCase();
    const exactMatch = suggestions.find((s) => s.query_normalized === normalized) ??
      recentHistory.find((s) => s.query_normalized === normalized);
    if (exactMatch && !exactMatch.is_sql_based && !exactMatch.needs_clarification && !isFailedResponse(exactMatch.formatted_response)) {
      analytics.trackAgentQuery({
        user: currentUser,
        query: userQuery,
        fromCache: true,
        isSqlBased: false,
        success: true,
        processingTimeMs: 0,
      });
      showCachedResult(exactMatch);
      return;
    }

    setQuery('');
    setLoading(true);
    setError(null);
    setResult(null);

    const userMessage: ConversationMessage = {
      role: 'user',
      text: userQuery,
      timestamp: new Date(),
    };
    const updatedConversation = [...conversation, userMessage];
    setConversation(updatedConversation);

    const startTime = Date.now();

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw new Error('Failed to get session');
      if (!session) throw new Error('Not authenticated. Please log in again.');

      const conversationHistory = conversation.map((msg) => ({
        role: msg.role,
        text: msg.text,
      }));

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Missing VITE_SUPABASE_URL');

      const response = await fetch(`${supabaseUrl}/functions/v1/agent-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({ query: userQuery, conversationHistory }),
      });

      const responseText = await response.text();

      if (!response.ok) {
        let errorMessage = `Request failed (HTTP ${response.status}).`;
        try {
          const errorBody = JSON.parse(responseText) as AgentErrorBody;
          errorMessage = friendlyAgentError(response.status, errorBody);
        } catch (e) {
          errorMessage = responseText ? `${errorMessage} ${responseText}` : errorMessage;
        }
        throw new Error(errorMessage);
      }

      const agentData: AgentQueryResponse = JSON.parse(responseText);
      setResult(agentData);

      const sqlQueries = agentData.result.steps
        ?.filter((s) => s.tool === 'run_sql') ?? [];

      analytics.trackAgentQuery({
        user: currentUser,
        query: userQuery,
        fromCache: false,
        isSqlBased: sqlQueries.length > 0,
        success: true,
        processingTimeMs: Date.now() - startTime,
      });

      if (agentData.result.formatted) {
        const assistantMessage: ConversationMessage = {
          role: 'assistant',
          text: agentData.result.formatted,
          timestamp: new Date(),
        };
        setConversation((prev) => [...prev, assistantMessage]);
        void saveToHistory(userQuery, agentData, conversation);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      analytics.trackAgentQuery({
        user: currentUser,
        query: userQuery,
        fromCache: false,
        isSqlBased: false,
        success: false,
        processingTimeMs: Date.now() - startTime,
        errorMessage,
      });
      setError(errorMessage);
      setConversation((prev) => [
        ...prev,
        { role: 'assistant', text: `Error: ${errorMessage}`, timestamp: new Date() },
      ]);
      console.error('Agent search error:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestion((i) => Math.min(i + 1, suggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestion((i) => Math.max(i - 1, -1));
        return;
      }
      if (e.key === 'Escape') {
        setSuggestions([]);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey && activeSuggestion >= 0) {
        e.preventDefault();
        const item = suggestions[activeSuggestion];
        setQuery(item.query);
        setSuggestions([]);
        void handleSearch(item.query);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !loading) {
      e.preventDefault();
      void handleSearch();
    }
  }

  function clearConversation() {
    setConversation([]);
    setResult(null);
    setError(null);
    setExportState({ status: 'idle' });
  }

  const exportSteps = extractRunSqlSteps(result?.result.steps);
  // Only offer export when at least one SQL step hit the 20-row display limit,
  // meaning the dataset was truncated and there may be more rows to retrieve.
  const SQL_UI_ROW_LIMIT = 20;
  const hasExportableData = (result?.result.steps ?? []).some((s) => {
    if (s.tool !== 'run_sql') return false;
    const r = s.result;
    if (!isRecord(r)) return false;
    const count = typeof r.count === 'number' ? r.count : (Array.isArray(r.rows) ? r.rows.length : 0);
    return count >= SQL_UI_ROW_LIMIT;
  });
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;

  async function handleExport() {
    if (exportSteps.length === 0 || exportState.status === 'loading') return;
    setExportState({ status: 'loading' });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const res = await fetch(`${supabaseUrl}/functions/v1/export-to-sheet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({ sqlSteps: exportSteps, queryText: lastSubmittedQuery }),
      });
      const data = await res.json() as { url?: string; tabs?: Array<{ name: string; rowCount: number; truncated: boolean }>; error?: string };
      if (!res.ok) throw new Error(data.error ?? 'Export failed');
      setExportState({ status: 'done', url: data.url ?? '', tabs: data.tabs ?? [] });
    } catch (err) {
      setExportState({ status: 'error', message: getErrorMessage(err) });
    }
  }

  const introExamples = [
    { label: 'Overdue invoices', query: 'Show me which invoices are overdue right now?' },
    { label: 'Recent payments', query: 'Show the 10 most recent payments.' },
    { label: 'Autopay enabled', query: 'How many parents have autopay enabled?' },
    { label: 'Enrollment Workflow', query: 'What does the enrollment workflow look like?' },
  ] as const;

  return (
    <AdminLayout>
      {/* The desktop shell is h-screen/overflow-hidden and `main` is a flex column with no
          overflow of its own, so each page owns its scroll container. Without this the
          results and debug panels below the fold were unreachable on desktop. */}
      <div className="p-4 sm:p-6 max-w-6xl mx-auto lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">✨ Genie (beta)</h1>
            <p className="text-gray-600">What would you like to look up today?</p>
          </div>
          <div className="flex items-center gap-2">
            {conversation.length > 0 && (
              <button
                onClick={clearConversation}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear
              </button>
            )}
            <button
              onClick={() => setHistoryOpen((o) => !o)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <History className="w-4 h-4" />
              History
              {recentHistory.length > 0 && (
                <span className="ml-1 bg-gray-200 text-gray-700 text-xs font-medium rounded-full px-1.5 py-0.5">
                  {recentHistory.length}
                </span>
              )}
              {historyOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
        </div>

        {/* History panel */}
        {historyOpen && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Recent queries (shared)</span>
            </div>
            {recentHistory.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-400">No history yet.</p>
            ) : (
              <div className="max-h-96 overflow-y-auto">
              <ul className="divide-y divide-gray-100">
                {recentHistory.map((item) => {
                  const failed = isFailedResponse(item.formatted_response);
                  const isExpanded = expandedHistoryId === item.id;
                  return (
                    <li key={item.id}>
                      {/* Row header */}
                      <div className="flex items-stretch">
                        <button
                          onClick={() => {
                            setQuery(item.query);
                            textareaRef.current?.focus();
                          }}
                          title="Copy query to input"
                          className="flex-1 text-left px-4 py-3 hover:bg-gray-50 flex items-start justify-between gap-3 min-h-[44px]"
                        >
                          <span className="text-sm text-gray-800 line-clamp-2 flex-1">{item.query}</span>
                          <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                            {failed ? (
                              <span className="inline-flex items-center gap-0.5 text-xs text-orange-600 bg-orange-50 rounded px-1.5 py-0.5">
                                Failed
                              </span>
                            ) : item.is_sql_based ? (
                              <span className="inline-flex items-center gap-0.5 text-xs text-blue-600 bg-blue-50 rounded px-1.5 py-0.5">
                                <Database className="w-3 h-3" /> SQL
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 text-xs text-green-600 bg-green-50 rounded px-1.5 py-0.5">
                                <Zap className="w-3 h-3" /> Static
                              </span>
                            )}
                            <span className="text-xs text-gray-400">{relativeTime(item.created_at)}</span>
                          </div>
                        </button>
                        <button
                          onClick={() => setExpandedHistoryId(isExpanded ? null : item.id)}
                          title={isExpanded ? 'Collapse' : 'Review previous response'}
                          className={`flex-shrink-0 px-3 flex items-center transition-colors ${
                            isExpanded ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' : 'text-gray-400 hover:text-blue-600 hover:bg-gray-50'
                          }`}
                        >
                          {isExpanded ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => void deleteHistoryItem(item.id)}
                          title="Delete"
                          className="flex-shrink-0 px-3 flex items-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {/* Inline review panel */}
                      {isExpanded && (
                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-sm text-gray-700">
                          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Previous response</p>
                          {renderMarkdown(item.formatted_response)}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              </div>
            )}
          </div>
        )}

        {/* Conversation */}
        <div
          className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6 overflow-y-auto ${
            conversation.length > 0 ? 'max-h-[600px]' : ''
          }`}
        >
          <div className="space-y-4">
            {conversation.length === 0 ? (
              <div className="flex gap-3 justify-start">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
                  <img src="/assets/favicon.ico" alt="Genie" className="w-6 h-6 object-contain" />
                </div>
                <div className="max-w-[80%] rounded-lg p-4 bg-gray-100 text-gray-900">
                  <div className="text-sm space-y-2">
                    <p className="font-medium">Hi — I’m Genie.</p>
                    <p className="text-gray-700">Ask me about students, enrollments, billing, and more.</p>
                    <div className="pt-1">
                      <div className="mt-2 flex flex-wrap gap-2 sm:hidden">
                        {introExamples.slice(0, 2).map((ex) => (
                          <button
                            key={ex.query}
                            onClick={() => void handleSearch(ex.query)}
                            className="px-3 py-1.5 text-xs rounded-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                            type="button"
                          >
                            {ex.label}
                          </button>
                        ))}
                      </div>
                      <div className="mt-2 hidden sm:flex flex-wrap gap-2">
                        {introExamples.map((ex) => (
                          <button
                            key={ex.query}
                            onClick={() => void handleSearch(ex.query)}
                            className="px-3 py-1.5 text-xs rounded-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                            type="button"
                          >
                            {ex.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs mt-2 text-gray-500">{introTimestamp.toLocaleTimeString()}</p>
                </div>
              </div>
            ) : (
              conversation.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
                      <img src="/assets/favicon.ico" alt="Genie" className="w-6 h-6 object-contain" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-lg p-4 ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="text-sm">
                      {msg.role === 'assistant' ? renderMarkdown(msg.text) : msg.text}
                    </div>
                    {/* Inline export button on the last assistant message when SQL was run */}
                    {msg.role === 'assistant' &&
                      idx === conversation.length - 1 &&
                      !loading &&
                      exportSteps.length > 0 &&
                      hasExportableData && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          {exportState.status === 'idle' && (
                            <button
                              onClick={() => void handleExport()}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 bg-white"
                            >
                              <FileSpreadsheet className="w-3.5 h-3.5" />
                              Export full dataset to Google Sheet
                            </button>
                          )}
                          {exportState.status === 'loading' && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              Exporting…
                            </div>
                          )}
                          {exportState.status === 'done' && (() => {
                            const totalRows = exportState.tabs.reduce((sum, t) => sum + t.rowCount, 0);
                            const anyTruncated = exportState.tabs.some((t) => t.truncated);
                            const label = exportState.tabs.length === 1
                              ? `${totalRows.toLocaleString()} rows`
                              : `${exportState.tabs.length} tabs · ${totalRows.toLocaleString()} rows`;
                            return (
                              <div className="flex flex-col gap-1">
                                <a
                                  href={exportState.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 border border-green-300 rounded-lg hover:bg-green-50 bg-white self-start"
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  {label} → Open Sheet
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                                {anyTruncated && (
                                  <p className="text-xs text-amber-600 flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    Some tabs truncated at 5,000 rows
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                          {exportState.status === 'error' && (
                            <p className="text-xs text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                              Export failed: {exportState.message}
                            </p>
                          )}
                        </div>
                      )}
                    <p className={`text-xs mt-2 ${msg.role === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                      {msg.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                  {msg.role === 'user' && (() => {
                    const meta = currentUser?.user_metadata;
                    const avatarUrl = (meta?.avatar_url as string | undefined) ?? (meta?.picture as string | undefined);
                    const name = (meta?.given_name as string | undefined) ??
                      (meta?.full_name as string | undefined)?.split(' ')[0] ??
                      (meta?.name as string | undefined)?.split(' ')[0] ??
                      currentUser?.email;
                    const initial = (name?.[0] ?? 'A').toUpperCase();
                    return avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={name ?? 'User'}
                        className="flex-shrink-0 w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-sm font-semibold text-indigo-700">{initial}</span>
                      </div>
                    );
                  })()}
                </div>
              ))
            )}
            {loading && (
              <div className="flex gap-3 justify-start">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
                  <img src="/assets/favicon.ico" alt="Genie" className="w-6 h-6 object-contain" />
                </div>
                <div className="bg-gray-100 rounded-lg p-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Search input */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex items-end gap-3">
            <div className="flex-1 relative">
              <div className="relative">
                <Search className="absolute left-3 top-3.5 text-gray-400 w-5 h-5" />
                <textarea
                  ref={textareaRef}
                  value={query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  placeholder={conversation.length > 0 ? 'Continue the conversation…' : 'Ask a question…'}
                  className="w-full resize-none overflow-hidden pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm leading-6 disabled:bg-gray-50"
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                  rows={1}
                />
              </div>

              {/* Autocomplete dropdown */}
              {suggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
                >
                  {suggestions.map((item, idx) => (
                    <button
                      key={item.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setQuery(item.query);
                        setSuggestions([]);
                        void handleSearch(item.query);
                      }}
                      className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 min-h-[44px] border-b border-gray-100 last:border-0 ${
                        idx === activeSuggestion ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-sm text-gray-800 truncate">{item.query}</span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {item.is_sql_based ? (
                          <span className="inline-flex items-center gap-0.5 text-xs text-blue-600 bg-blue-50 rounded px-1.5 py-0.5">
                            <Database className="w-3 h-3" /> SQL
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 text-xs text-green-600 bg-green-50 rounded px-1.5 py-0.5">
                            <Zap className="w-3 h-3" /> Cached
                          </span>
                        )}
                        <span className="text-xs text-gray-400">{relativeTime(item.created_at)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => void handleSearch()}
              disabled={loading || !query.trim()}
              className="h-12 w-12 sm:h-auto sm:w-auto sm:px-6 sm:py-3 flex items-center justify-center gap-2 rounded-full sm:rounded-lg bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Send"
              title="Send"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="hidden sm:inline">Sending...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span className="hidden sm:inline">Send</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-800 mb-1">Error</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Results display */}
        {result && (
          <div className="space-y-6">
            {result.result.formatted && conversation.length === 0 && (
              <div className={`rounded-lg p-6 ${
                result.result.needsClarification
                  ? 'bg-yellow-50 border border-yellow-200'
                  : 'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-start gap-3 mb-2">
                  {result.result.needsClarification ? (
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  )}
                  <h2 className="text-xl font-semibold text-gray-900">
                    {result.result.needsClarification ? 'Clarification Needed' : 'Answer'}
                  </h2>
                </div>
                <div className={`mt-2 ${result.result.needsClarification ? 'text-yellow-800' : 'text-gray-700'}`}>
                  {renderMarkdown(result.result.formatted)}
                </div>
              </div>
            )}

            {/* Student table */}
            {result.result.students && result.result.students.length > 0 && (
              <details className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <summary className="px-6 py-4 bg-gray-50 border-b border-gray-200 cursor-pointer flex items-center justify-between hover:bg-gray-100 list-none">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Students (first {result.result.count || result.result.students.length})
                  </h2>
                  <ChevronDown className="w-4 h-4 text-gray-400 details-chevron" />
                </summary>
                <div className="overflow-x-auto overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parents</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enrollments</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {result.result.students.map((student) => (
                        <tr key={student.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {student.first_name} {student.last_name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              student.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {student.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {student.parents.length > 0 ? (
                                <ul className="list-disc list-inside">
                                  {student.parents.map((parent, idx) => (
                                    <li key={idx}>
                                      {parent.first_name} {parent.last_name} ({parent.relationship})
                                      <br />
                                      <span className="text-gray-500 text-xs">{parent.email}</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-gray-400">No parents</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {student.enrollments.length > 0 ? (
                                <ul className="list-disc list-inside">
                                  {student.enrollments.map((enrollment, idx) => (
                                    <li key={idx}>
                                      {enrollment.program_name} ({enrollment.program_type})
                                      {enrollment.is_active && (
                                        <span className="ml-2 text-xs text-green-600">Active</span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-gray-400">No enrollments</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {/* Generic rows table */}
            {(!result.result.students || result.result.students.length === 0) &&
              Array.isArray(result.result.rows) &&
              result.result.rows.length > 0 && (
                <details className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <summary className="px-6 py-4 bg-gray-50 border-b border-gray-200 cursor-pointer flex items-center justify-between hover:bg-gray-100 list-none">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {result.result.table ? `${result.result.table} ` : ''}
                      Results (first {result.result.count || result.result.rows.length})
                    </h2>
                    <ChevronDown className="w-4 h-4 text-gray-400 details-chevron" />
                  </summary>
                  <div className="overflow-x-auto overflow-y-auto">
                    {isRecord(result.result.rows[0]) ? (
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            {Object.keys(result.result.rows[0]).map((col) => (
                              <th
                                key={col}
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {result.result.rows.map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              {isRecord(row) ? (
                                Object.keys(result.result.rows?.[0] as Record<string, unknown>).map((col) => (
                                  <td key={col} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {String((row as Record<string, unknown>)[col] ?? '')}
                                  </td>
                                ))
                              ) : (
                                <td className="px-6 py-4 text-sm text-gray-900">{String(row)}</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <pre className="p-4 text-xs overflow-x-auto">{JSON.stringify(result.result.rows, null, 2)}</pre>
                    )}
                  </div>
                </details>
              )}

            {/* Export to Google Sheet */}
            {exportSteps.length > 0 && hasExportableData && (
              <div className="flex flex-col gap-2">
                {exportState.status === 'idle' && (
                  <button
                    onClick={() => void handleExport()}
                    className="self-start flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Export to Google Sheet
                  </button>
                )}
                {exportState.status === 'loading' && (
                  <div className="self-start flex items-center gap-2 px-4 py-2 text-sm text-gray-500 border border-gray-200 rounded-lg bg-gray-50">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Exporting…
                  </div>
                )}
                {exportState.status === 'done' && (() => {
                  const totalRows = exportState.tabs.reduce((sum, t) => sum + t.rowCount, 0);
                  const anyTruncated = exportState.tabs.some((t) => t.truncated);
                  const label = exportState.tabs.length === 1
                    ? `${totalRows.toLocaleString()} rows`
                    : `${exportState.tabs.length} tabs · ${totalRows.toLocaleString()} total rows`;
                  return (
                    <div className="flex flex-col gap-1">
                      <a
                        href={exportState.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="self-start flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-700 border border-green-300 rounded-lg hover:bg-green-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {label} → Open Sheet
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      {anyTruncated && (
                        <p className="text-xs text-amber-600 flex items-center gap-1 pl-1">
                          <AlertCircle className="w-3.5 h-3.5" />
                          Some tabs were truncated at 5,000 rows
                        </p>
                      )}
                    </div>
                  );
                })()}
                {exportState.status === 'error' && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    Export failed: {exportState.message}
                  </p>
                )}
              </div>
            )}

            {/* Debug info */}
            <details className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                Debug Information
              </summary>
              <div className="mt-4 space-y-2">
                <div>
                  <span className="text-xs font-medium text-gray-500">Queries Executed:</span>
                  <pre className="mt-1 text-xs bg-white p-2 rounded border overflow-x-auto">
                    {JSON.stringify(result.queriesExecuted, null, 2)}
                  </pre>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500">Reasoning:</span>
                  <pre className="mt-1 text-xs bg-white p-2 rounded border overflow-x-auto">
                    {JSON.stringify(result.reasoning, null, 2)}
                  </pre>
                </div>
              </div>
            </details>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
